import apiClient from '../axios.js';

/**
 * LINE 官方帳號設定管理 API
 */
export const lineService = {
    /**
     * 獲取 LINE 設定列表
     * @param {Object} params - 查詢參數
     * @param {number} params.page - 頁碼
     * @param {number} params.pageSize - 每頁數量
     * @param {boolean} params.isActive - 是否啟用（可選）
     * @param {string} params.status - 狀態（可選：active, inactive, testing）
     * @param {string} params.search - 搜尋關鍵字（可選）
     * @returns {Promise<Object>} LINE 設定列表
     */
    getConfigs: async (params = {}) => {
        const response = await apiClient.get('/line/config', { params });
        return response;
    },

    /**
     * 獲取單一 LINE 設定詳情
     * @param {string} id - 設定 ID
     * @param {boolean} decrypt - 是否返回解密後的值（用於編輯頁面）
     * @returns {Promise<Object>} LINE 設定詳情
     */
    getConfig: async (id, decrypt = false) => {
        const params = decrypt ? { decrypt: 'true' } : {};
        const response = await apiClient.get(`/line/config/${id}`, { params });
        return response;
    },

    /**
     * 建立 LINE 設定
     * @param {Object} configData - 設定資料
     * @param {string} configData.configName - 設定名稱
     * @param {string} configData.messagingChannelId - Messaging Channel ID
     * @param {string} configData.messagingChannelSecret - Messaging Channel Secret
     * @param {string} configData.messagingBotBasicId - Bot Basic ID
     * @param {string} configData.messagingAccessToken - Channel Access Token
     * @param {string} configData.messagingAccessTokenExpiresAt - Token 過期時間（可選）
     * @param {string} configData.loginChannelId - LINE Login Channel ID
     * @param {string} configData.loginChannelSecret - LINE Login Channel Secret
     * @param {string} configData.loginCallbackUrl - Callback URL
     * @param {Array<string>} configData.liffIds - LIFF ID 列表（可選）
     * @param {boolean} configData.isActive - 是否啟用（可選，預設 true）
     * @param {string} configData.status - 狀態（可選，預設 inactive）
     * @returns {Promise<Object>} 建立的設定
     */
    createConfig: async (configData) => {
        const response = await apiClient.post('/line/config', configData);
        return response;
    },

    /**
     * 更新 LINE 設定
     * @param {string} id - 設定 ID
     * @param {Object} configData - 更新資料
     * @returns {Promise<Object>} 更新的設定
     */
    updateConfig: async (id, configData) => {
        const response = await apiClient.put(`/line/config/${id}`, configData);
        return response;
    },

    /**
     * 刪除 LINE 設定
     * @param {string} id - 設定 ID
     * @returns {Promise<Object>} 刪除結果
     */
    deleteConfig: async (id) => {
        const response = await apiClient.delete(`/line/config/${id}`);
        return response;
    },

    /**
     * 測試 LINE 連線
     * @param {string} id - 設定 ID
     * @returns {Promise<Object>} 測試結果
     */
    testConnection: async (id) => {
        const response = await apiClient.post(`/line/config/${id}/test`);
        return response;
    },

    /**
     * 啟用/停用 LINE 設定
     * @param {string} id - 設定 ID
     * @param {boolean} isActive - 是否啟用
     * @returns {Promise<Object>} 更新結果
     */
    updateStatus: async (id, isActive) => {
        const response = await apiClient.patch(`/line/config/${id}/status`, { isActive });
        return response;
    },

    /**
     * 更新 Webhook 轉送設定
     * @param {string} id - 設定 ID
     * @param {Object} data - { enabled, forwardUrl, forwardSecret }
     * @returns {Promise<Object>} 更新結果
     */
    updateWebhookForward: async (id, data) => {
        const response = await apiClient.patch(`/line/config/${id}/webhook-forward`, data);
        return response;
    },

    /**
     * 更新 Channel Access Token
     * @param {string} id - 設定 ID
     * @param {string} accessToken - 新的 Access Token
     * @param {string} expiresAt - Token 過期時間（ISO 8601 格式）
     * @returns {Promise<Object>} 更新結果
     */
    refreshToken: async (id, accessToken, expiresAt) => {
        const response = await apiClient.post(`/line/config/${id}/refresh-token`, {
            accessToken,
            expiresAt
        });
        return response;
    },

    /**
     * 獲取變更記錄
     * @param {string} id - 設定 ID
     * @param {Object} params - 查詢參數
     * @param {number} params.page - 頁碼
     * @param {number} params.pageSize - 每頁數量
     * @param {string} params.changeType - 變更類型（可選：created, updated, deleted, activated, deactivated）
     * @returns {Promise<Object>} 變更記錄列表
     */
    getChangeLogs: async (id, params = {}) => {
        const response = await apiClient.get(`/line/config/${id}/change-logs`, { params });
        return response;
    }
};
